/*
  <ilura-header> — the shop mega panel and the mobile sheet.

  Dawn's menu-drawer is built around its own <details>/<summary> markup and
  arbitrary-depth menus, so owning this header meant owning these two
  interactions. trapFocus, removeTrapFocus and the overflow-hidden body class
  come from Dawn's global.js, which is what Dawn's own drawers use — there is
  no second focus-trap implementation in this theme.

  Both surfaces carry the hidden attribute at rest, so they stay shut with no
  JavaScript at all. The sheet removes hidden first and sets data-open on the
  next frame, because an element with display:none has no box to transition a
  clip-path from.

  Every window-level listener is removed in disconnectedCallback. The theme
  editor replaces a section's DOM on each setting change, and a header that
  registered a pageshow handler per connect would accumulate one per edit.
*/
if (!customElements.get('ilura-header')) {
  class IluraHeader extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
      this.onPageshow = this.onPageshow.bind(this);
      this.onFocusin = this.onFocusin.bind(this);
      this.closeSheet = this.closeSheet.bind(this);
      this.closeMega = this.closeMega.bind(this);
    }

    connectedCallback() {
      this.mega = this.querySelector('[data-mega]');
      this.megaToggle = this.querySelector('[data-mega-toggle]');
      this.megaItem = this.megaToggle ? this.megaToggle.closest('.ilura-nav__item') : null;
      this.sheet = this.querySelector('[data-sheet]');
      this.burger = this.querySelector('[data-sheet-open]');

      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('pageshow', this.onPageshow);
      document.addEventListener('focusin', this.onFocusin);

      this.connectMega();
      this.connectSheet();
    }

    disconnectedCallback() {
      this.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('pageshow', this.onPageshow);
      document.removeEventListener('focusin', this.onFocusin);
      document.body.classList.remove('overflow-hidden');
    }

    /* ---- shop mega panel ---- */

    connectMega() {
      if (!this.mega || !this.megaToggle) return;

      this.megaToggle.addEventListener('click', () => {
        if (this.mega.hidden) this.openMega();
        else this.closeMega();
      });

      /*
        Pointer opening is bound to the nav item and to the panel, and closing
        to the pointer leaving the header altogether. What is deliberately NOT
        bound is the bar's own empty space: the pointer has to cross the gap
        between the link and the panel below it, and closing on that would make
        the panel unreachable by the gesture that opens it.
      */
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      const open = () => this.openMega();
      if (this.megaItem) this.megaItem.addEventListener('pointerenter', open);
      this.mega.addEventListener('pointerenter', open);
      this.addEventListener('pointerleave', this.closeMega);
    }

    openMega() {
      if (!this.mega) return;
      this.mega.hidden = false;
      this.megaToggle.setAttribute('aria-expanded', 'true');
    }

    closeMega() {
      if (!this.mega || this.mega.hidden) return;
      this.mega.hidden = true;
      this.megaToggle.setAttribute('aria-expanded', 'false');
    }

    /* ---- mobile sheet ---- */

    connectSheet() {
      if (!this.sheet || !this.burger) return;

      this.burger.addEventListener('click', () => this.openSheet());
      this.querySelectorAll('[data-sheet-close]').forEach((button) => {
        button.addEventListener('click', this.closeSheet);
      });
    }

    openSheet() {
      if (!this.sheet) return;
      this.sheet.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.burger.setAttribute('aria-expanded', 'true');

      requestAnimationFrame(() => {
        this.sheet.dataset.open = 'true';
        const first = this.sheet.querySelector('a, button');
        trapFocus(this.sheet, first || this.sheet);
      });
    }

    closeSheet() {
      if (!this.sheet || this.sheet.hidden) return;

      this.sheet.dataset.open = 'false';
      document.body.classList.remove('overflow-hidden');
      this.burger.setAttribute('aria-expanded', 'false');
      removeTrapFocus(this.burger);

      const finish = () => {
        this.sheet.hidden = true;
      };

      /* Branch rather than rely on transitionend firing: with reduced motion
         the transition is removed in CSS, so the event never arrives and the
         sheet would stay in the accessibility tree. */
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
      } else {
        this.sheet.addEventListener('transitionend', finish, { once: true });
      }
    }

    /* ---- shared ---- */

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      this.closeMega();
      this.closeSheet();
    }

    onFocusin(event) {
      if (!this.mega || this.mega.hidden) return;
      if (!this.contains(event.target)) this.closeMega();
    }

    /* A link tap should leave both shut even when the browser serves the next
       page from bfcache and never re-runs connectedCallback. */
    onPageshow() {
      this.closeMega();
      this.closeSheet();
    }
  }

  customElements.define('ilura-header', IluraHeader);
}
